// npm
import React from 'react'
import PropTypes from 'prop-types'
import {IdGenerator} from '@xams-utils/id-generator'

// material-ui
import withStyles from '@material-ui/core/styles/withStyles'
import DragHandle from '@material-ui/icons/DragHandle'

// react-konva
import {Stage, Layer, Line, Transformer} from 'react-konva'

// react
import {BoundsListener} from 'components/functional/bounds-listener'
import {Toolbar} from './toolbar'
import {ShapeRenderer} from './shape-renderer'
import {LabelEditor} from './label-editor'
import {exportAndUploadDrawing} from './export-service'

// constants
import {TOOLS, DEFAULT_STROKE, DEFAULT_STROKE_WIDTH, GRID_SIZE, GRID_COLOUR, MIN_SHAPE_SIZE} from './constants'

const EXPORT_DEBOUNCE_MS = 2000;
const MIN_STAGE_HEIGHT = 200;
const DEFAULT_STAGE_HEIGHT = 350;


// DrawingCanvas (not connected to styles)
// --------------------------------------------------------------------------

class DrawingCanvas extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			activeTool: TOOLS.PEN,
			selectedId: null,
			draftShape: null,
			dragStart: null,
			editingLabel: null,		// {id (null for a new label), x, y, text, ...} while the label editor is open
			width: 0,
			height: DEFAULT_STAGE_HEIGHT
		};

		this.stageRef = React.createRef();
		this.transformerRef = React.createRef();
		this.labelEditorRef = React.createRef();
		this.shapeNodes = {};
		this.exportTimeoutId = null;

		this.handleWidthChange = this.handleWidthChange.bind(this);
		this.handleResizeStart = this.handleResizeStart.bind(this);
		this.handleSelectTool = this.handleSelectTool.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.handleToggleGrid = this.handleToggleGrid.bind(this);
		this.handleUndo = this.handleUndo.bind(this);
		this.handleClear = this.handleClear.bind(this);
		this.handleDeleteSelected = this.handleDeleteSelected.bind(this);
		this.handleLabelCommit = this.handleLabelCommit.bind(this);
		this.handleLabelCancel = this.handleLabelCancel.bind(this);
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.selectedId !== this.state.selectedId) {
			this.updateTransformer();
		}

		if (prevProps.drawing.shapes !== this.props.drawing.shapes) {
			this.scheduleExport();
		}

		// a label open on one question must not carry over onto the next question's drawing
		if (prevProps.questionId !== this.props.questionId && this.state.editingLabel) {
			this.setState({editingLabel: null});
		}
	}

	componentWillUnmount() {
		clearTimeout(this.exportTimeoutId);
		document.removeEventListener('mousemove', this._onResizeMouseMove);
		document.removeEventListener('mouseup', this._onResizeMouseUp);
	}

	// debounced so a burst of edits (a pen stroke, several shapes) only exports once things
	// settle down, rather than re-uploading a PNG on every intermediate change
	scheduleExport() {
		clearTimeout(this.exportTimeoutId);
		this.exportTimeoutId = setTimeout(() => {
			const {formRunGuid, questionId, onImageUploaded} = this.props;

			exportAndUploadDrawing(this.stageRef.current, formRunGuid, questionId).then((imageId) => {
				if (imageId) {
					onImageUploaded(imageId);
				}
			});
		}, EXPORT_DEBOUNCE_MS);
	}

	updateTransformer() {
		const transformer = this.transformerRef.current;
		if (!transformer) {
			return;
		}

		const {selectedId} = this.state;
		const node = selectedId ? this.shapeNodes[selectedId] : null;

		// this Konva version's Transformer only supports single-node attach/detach,
		// not the array-based multi-select `.nodes()` API added in later Konva releases
		if (node) {
			transformer.attachTo(node);
		} else {
			transformer.detach();
		}
		transformer.getLayer().batchDraw();
	}

	// only width tracks the container's natural (auto) size - height is user-controlled via
	// the resize handle below, so it isn't overwritten by the container's own measured height
	handleWidthChange(width) {
		this.setState({width});
	}

	handleResizeStart(e) {
		e.preventDefault();
		const startY = e.clientY;
		const startHeight = this.state.height;

		this._onResizeMouseMove = (e) => {
			const newHeight = Math.max(MIN_STAGE_HEIGHT, startHeight + (e.clientY - startY));
			this.setState({height: newHeight});
		};

		this._onResizeMouseUp = () => {
			document.removeEventListener('mousemove', this._onResizeMouseMove);
			document.removeEventListener('mouseup', this._onResizeMouseUp);
		};

		document.addEventListener('mousemove', this._onResizeMouseMove);
		document.addEventListener('mouseup', this._onResizeMouseUp);
	}

	handleSelectTool(tool) {
		this.setState({activeTool: tool, selectedId: null});
	}

	getPointerPosition() {
		const stage = this.stageRef.current;
		return stage ? stage.getPointerPosition() : null;
	}

	handlePointerDown(e) {
		const {activeTool, editingLabel} = this.state;

		// clicking the stage while a label is open just finishes that label, rather than also
		// starting a new shape/label at the click point
		if (editingLabel) {
			e.evt.preventDefault();
			const editor = this.labelEditorRef.current;
			if (editor) {
				editor.commit();
			}
			return;
		}

		if (activeTool === TOOLS.SELECT) {
			// clicking empty stage space clears selection; clicking a shape is handled by
			// that shape's own onSelect (see ShapeRenderer)
			if (e.target === e.target.getStage()) {
				this.setState({selectedId: null});
			}
			return;
		}

		const pos = this.getPointerPosition();
		if (!pos) {
			return;
		}

		if (activeTool === TOOLS.TEXT) {
			// stops the browser's default mousedown focus handling from immediately blurring
			// (and so committing) the editor that's about to open
			e.evt.preventDefault();
			this.handlePlaceText(e.target, pos);
			return;
		}

		const id = IdGenerator.uuid();
		const stroke = DEFAULT_STROKE;
		const strokeWidth = DEFAULT_STROKE_WIDTH;

		if (activeTool === TOOLS.PEN) {
			this.setState({draftShape: {id, type: TOOLS.PEN, points: [pos.x, pos.y], stroke, strokeWidth}});
		} else if (activeTool === TOOLS.LINE || activeTool === TOOLS.ARROW) {
			this.setState({draftShape: {id, type: activeTool, points: [pos.x, pos.y, pos.x, pos.y], stroke, strokeWidth}});
		} else if (activeTool === TOOLS.RECT || activeTool === TOOLS.DIAMOND) {
			this.setState({
				draftShape: {id, type: activeTool, x: pos.x, y: pos.y, width: 0, height: 0, stroke, strokeWidth},
				dragStart: pos
			});
		} else if (activeTool === TOOLS.CIRCLE) {
			this.setState({
				draftShape: {id, type: activeTool, x: pos.x, y: pos.y, radiusX: 0, radiusY: 0, stroke, strokeWidth},
				dragStart: pos
			});
		}
	}

	handlePointerMove() {
		const {draftShape, activeTool, dragStart} = this.state;
		if (!draftShape) {
			return;
		}

		const pos = this.getPointerPosition();
		if (!pos) {
			return;
		}

		if (activeTool === TOOLS.PEN) {
			this.setState({draftShape: {...draftShape, points: [...draftShape.points, pos.x, pos.y]}});
		} else if (activeTool === TOOLS.LINE || activeTool === TOOLS.ARROW) {
			const points = [draftShape.points[0], draftShape.points[1], pos.x, pos.y];
			this.setState({draftShape: {...draftShape, points}});
		} else if (activeTool === TOOLS.RECT || activeTool === TOOLS.DIAMOND) {
			this.setState({
				draftShape: {
					...draftShape,
					x: Math.min(dragStart.x, pos.x),
					y: Math.min(dragStart.y, pos.y),
					width: Math.abs(pos.x - dragStart.x),
					height: Math.abs(pos.y - dragStart.y)
				}
			});
		} else if (activeTool === TOOLS.CIRCLE) {
			this.setState({
				draftShape: {
					...draftShape,
					x: (dragStart.x + pos.x) / 2,
					y: (dragStart.y + pos.y) / 2,
					radiusX: Math.abs(pos.x - dragStart.x) / 2,
					radiusY: Math.abs(pos.y - dragStart.y) / 2
				}
			});
		}
	}

	handlePointerUp() {
		const {draftShape, activeTool} = this.state;
		if (!draftShape) {
			return;
		}

		const bigEnough = this.isDraftShapeBigEnough(draftShape, activeTool);
		this.setState({draftShape: null, dragStart: null});

		if (bigEnough) {
			this.commitShape(draftShape);
		}
	}

	isDraftShapeBigEnough(shape, tool) {
		if (tool === TOOLS.PEN) {
			return shape.points.length > 2;
		}
		if (tool === TOOLS.LINE || tool === TOOLS.ARROW) {
			const [x1, y1, x2, y2] = shape.points;
			return Math.hypot(x2 - x1, y2 - y1) >= MIN_SHAPE_SIZE;
		}
		if (tool === TOOLS.RECT || tool === TOOLS.DIAMOND) {
			return shape.width >= MIN_SHAPE_SIZE && shape.height >= MIN_SHAPE_SIZE;
		}
		if (tool === TOOLS.CIRCLE) {
			return shape.radiusX >= MIN_SHAPE_SIZE / 2 && shape.radiusY >= MIN_SHAPE_SIZE / 2;
		}
		return true;
	}

	// with the Text tool, clicking an existing label edits it; clicking anywhere else opens the
	// editor for a new label at that point
	handlePlaceText(target, pos) {
		const existing = this.findLabel(target.id());
		if (existing) {
			this.handleEditLabel(existing);
			return;
		}

		this.setState({editingLabel: {id: null, x: pos.x, y: pos.y, text: '', stroke: DEFAULT_STROKE}, selectedId: null});
	}

	findLabel(id) {
		return id ? this.props.drawing.shapes.find((shape) => shape.id === id && shape.type === TOOLS.TEXT) : null;
	}

	handleEditLabel(shape) {
		this.setState({editingLabel: shape, selectedId: null});
	}

	handleLabelCommit(value) {
		const {editingLabel} = this.state;
		if (!editingLabel) {
			return;
		}
		this.setState({editingLabel: null});

		// trailing blank lines/spaces are never intentional, and would only pad the label's bounds
		const text = value.replace(/\s+$/, '');
		const {drawing, onChange} = this.props;

		if (!editingLabel.id) {
			if (text) {
				this.commitShape({id: IdGenerator.uuid(), type: TOOLS.TEXT, x: editingLabel.x, y: editingLabel.y, text, stroke: DEFAULT_STROKE});
			}
		} else if (!text) {
			// emptying an existing label removes it, rather than leaving an invisible shape behind
			onChange({...drawing, shapes: drawing.shapes.filter((shape) => shape.id !== editingLabel.id)});
		} else if (text !== editingLabel.text) {
			onChange({...drawing, shapes: drawing.shapes.map((shape) => (shape.id === editingLabel.id ? {...shape, text} : shape))});
		}
	}

	handleLabelCancel() {
		this.setState({editingLabel: null});
	}

	commitShape(shape) {
		const {drawing, onChange} = this.props;
		onChange({...drawing, shapes: [...drawing.shapes, shape]});
	}

	handleSelectShape(id) {
		this.setState({selectedId: id});
	}

	handleShapeChange(id, node) {
		const {drawing, onChange} = this.props;
		const shapes = drawing.shapes.map((shape) => (shape.id !== id ? shape : {
			...shape,
			x: node.x(),
			y: node.y(),
			rotation: node.rotation(),
			scaleX: node.scaleX(),
			scaleY: node.scaleY()
		}));

		onChange({...drawing, shapes});
	}

	handleToggleGrid() {
		const {drawing, onChange} = this.props;
		onChange({...drawing, showGrid: !drawing.showGrid});
	}

	handleUndo() {
		const {drawing, onChange} = this.props;
		onChange({...drawing, shapes: drawing.shapes.slice(0, -1)});
	}

	handleClear() {
		const {drawing, onChange} = this.props;
		this.setState({selectedId: null});
		onChange({...drawing, shapes: []});
	}

	handleDeleteSelected() {
		const {selectedId} = this.state;
		if (!selectedId) {
			return;
		}

		const {drawing, onChange} = this.props;
		onChange({...drawing, shapes: drawing.shapes.filter((shape) => shape.id !== selectedId)});
		this.setState({selectedId: null});
	}

	getGridLines() {
		const {width, height} = this.state;
		const lines = [];

		for (let x = 0; x <= width; x += GRID_SIZE) {
			lines.push(<Line key={`v${x}`} points={[x, 0, x, height]} stroke={GRID_COLOUR} strokeWidth={1}/>);
		}
		for (let y = 0; y <= height; y += GRID_SIZE) {
			lines.push(<Line key={`h${y}`} points={[0, y, width, y]} stroke={GRID_COLOUR} strokeWidth={1}/>);
		}

		return lines;
	}

	render() {
		const {classes, drawing} = this.props;
		const {activeTool, selectedId, draftShape, editingLabel, width, height} = this.state;
		const interactive = activeTool === TOOLS.SELECT;
		const committedShapes = draftShape ? [...drawing.shapes, draftShape] : drawing.shapes;
		// the label being edited is hidden while its editor sits on top, so it isn't shown twice
		const shapes = editingLabel && editingLabel.id
			? committedShapes.filter((shape) => shape.id !== editingLabel.id)
			: committedShapes;
		const cursor = activeTool === TOOLS.TEXT ? 'text' : (interactive ? 'default' : 'crosshair');

		const toolbarProps = {
			activeTool,
			onSelectTool: this.handleSelectTool,
			showGrid: drawing.showGrid,
			onToggleGrid: this.handleToggleGrid,
			onUndo: this.handleUndo,
			canUndo: drawing.shapes.length > 0,
			onClear: this.handleClear,
			hasSelection: !!selectedId,
			onDeleteSelected: this.handleDeleteSelected
		};

		return (
			<div className={classes.container}>
				<Toolbar {...toolbarProps}/>
				<BoundsListener onSizeChange={this.handleWidthChange}>
					<div className={classes.stageContainer} style={{height, cursor}}>
						<Stage
							ref={this.stageRef}
							width={width}
							height={height}
							onMouseDown={this.handlePointerDown}
							onMouseMove={this.handlePointerMove}
							onMouseUp={this.handlePointerUp}
							onTouchStart={this.handlePointerDown}
							onTouchMove={this.handlePointerMove}
							onTouchEnd={this.handlePointerUp}
						>
							<Layer listening={false}>
								{drawing.showGrid && this.getGridLines()}
							</Layer>
							<Layer>
								{shapes.map((shape) => (
									<ShapeRenderer
										key={shape.id}
										shape={shape}
										interactive={interactive}
										shapeRef={(node) => {
											this.shapeNodes[shape.id] = node;
										}}
										onSelect={() => this.handleSelectShape(shape.id)}
										onChange={(e) => this.handleShapeChange(shape.id, e.target)}
										onEdit={() => this.handleEditLabel(shape)}
									/>
								))}
								{interactive && <Transformer ref={this.transformerRef}/>}
							</Layer>
						</Stage>
						{editingLabel && (
							<LabelEditor
								key={editingLabel.id || 'new'}
								innerRef={this.labelEditorRef}
								label={editingLabel}
								onCommit={this.handleLabelCommit}
								onCancel={this.handleLabelCancel}
							/>
						)}
					</div>
				</BoundsListener>
				<div className={classes.resizeHandle} onMouseDown={this.handleResizeStart}>
					<DragHandle className={classes.resizeIcon}/>
				</div>
			</div>
		);
	}
}

DrawingCanvas.propTypes = {
	drawing: PropTypes.shape({
		shapes: PropTypes.array.isRequired,
		showGrid: PropTypes.bool.isRequired
	}).isRequired,
	onChange: PropTypes.func.isRequired,
	formRunGuid: PropTypes.string,
	questionId: PropTypes.number,
	onImageUploaded: PropTypes.func.isRequired
};


// DrawingCanvas (connected to styles)
// --------------------------------------------------------------------------

const styles = ({palette}) => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		minHeight: 0
	},
	stageContainer: {
		// anchors the absolutely-positioned label editor to stage coordinates
		position: 'relative',
		flexShrink: 0,
		width: '100%',
		minWidth: 0,
		// the Konva stage sets an explicit pixel width on its own DOM node - without this,
		// a not-yet-shrunk canvas can force its containers to stay wide instead of clipping,
		// which also makes the BoundsListener measurement echo the stale (too-wide) size back
		overflow: 'hidden',
		backgroundColor: palette.background.paper
	},
	resizeHandle: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 14,
		width: '100%',
		backgroundColor: palette.background.default,
		borderTop: `1px solid ${palette.background.contrastText}`,
		cursor: 'row-resize',
		userSelect: 'none'
	},
	resizeIcon: {
		width: 20,
		height: 14,
		color: palette.background.contrastText
	}
});

DrawingCanvas = withStyles(styles)(DrawingCanvas);


// Export
// --------------------------------------------------------------------------
export {DrawingCanvas}
